import React, { useReducer, useState } from "react";
import PropTypes from "prop-types";
import AlertWarningTable from "../../elements/Tables/AlertWarningTable/AlertWarningTable";
import AlertWarningCreationForm from "../../elements/Forms/AlertWarningCreationForm/AlertWarningCreationForm";

import { useFetch } from "../../../Utils/useFetch";

import {
  FETCH_ALL_ALERTWARNING_API,
  FETCH_ALL_COUNTER_API,
} from "../../../Constants/Apis";
import { Button, Input, message, Modal, Upload, Table } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import * as XLSX from "xlsx";
import { reducer } from "../../../Utils/CommonUtils";
import { deleteAlertWarning, bulkUpsertAlertWarnings, bulkDeleteAlertWarnings } from "../../../Services/AlertWarningService";

const { Search } = Input;

const AlertWarningPage = ({ history, userData }) => {
  const initialState = {
    searchKeyword: "",
    filteredInfo: {},
    sorter: {},
  };

  const [state, dispatch] = useReducer(reducer, initialState);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [alertWarningId, setAlertWarningId] = useState(null);
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  const [bulkRows, setBulkRows] = useState([]);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [isBulkDeleteModalOpen, setIsBulkDeleteModalOpen] = useState(false);
  const [bulkDeleteRows, setBulkDeleteRows] = useState([]);

  const handleOpenModal = () => setIsModalVisible(true);
  const handleCloseModal = () => setIsModalVisible(false);

  const handleDelete = async (id) => {
    setAlertWarningId(id);
    setIsModalOpen(true);
  };

  const handleOk = async () => {
    try {
      await deleteAlertWarning(alertWarningId);
      message.success("AlertWarning deleted successfully");
      callReFetch();
    } catch (error) {
      message.error(
        error?.response?.data?.message ||
          "Something went wrong, failed to delete alert warning."
      );
    }
    setIsModalOpen(false);
  };

  const parseFile = async (file) => {
    const accepted = [".xlsx", ".xls", ".csv"];
    const ext = file.name.substring(file.name.lastIndexOf(".")).toLowerCase();
    if (!accepted.includes(ext)) {
      message.error("Please upload a valid Excel or CSV file");
      return false;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: "array" });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        const raw = XLSX.utils.sheet_to_json(sheet, { raw: false });
        const rows = (raw || [])
          .map((r) => ({
            sku: String(r.sku || r.SKU || r.Sku || r.SkU || "").trim(),
            threshold: Number(r.threshold ?? r.Threshold ?? r.THRESHOLD),
          }))
          .filter((r) => r.sku && !Number.isNaN(r.threshold));
        if (!rows.length) {
          message.warning("No valid rows found. Expect columns: sku, threshold");
        }
        setBulkRows(rows);
      } catch (err) {
        console.error(err);
        message.error("Failed to parse file");
      }
    };
    reader.readAsArrayBuffer(file);
    return false; // prevent upload
  };

  const handleOpenBulk = () => {
    setBulkRows([]);
    setIsBulkModalOpen(true);
  };

  const handleBulkSubmit = async () => {
    if (!bulkRows.length) {
      message.warning("Please upload a file with data first");
      return;
    }
    setBulkSubmitting(true);
    try {
      await bulkUpsertAlertWarnings({ items: bulkRows, counterCode: "ECOMMRI" });
      message.success("Bulk upsert completed");
      setIsBulkModalOpen(false);
      setBulkRows([]);
      callReFetch();
    } catch (e) {
      message.error("Bulk upsert failed");
    } finally {
      setBulkSubmitting(false);
    }
  };

  const parseDeleteFile = async (file) => {
    const accepted = [".xlsx", ".xls", ".csv"];
    const ext = file.name.substring(file.name.lastIndexOf(".")).toLowerCase();
    if (!accepted.includes(ext)) {
      message.error("Please upload a valid Excel or CSV file");
      return false;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: "array" });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        const raw = XLSX.utils.sheet_to_json(sheet, { raw: false });
        const rows = (raw || [])
          .map((r) => ({
            sku: String(r.sku || r.SKU || r.Sku || "").trim(),
          }))
          .filter((r) => r.sku);
        if (!rows.length) {
          message.warning("No valid rows found. Expect columns: sku");
        }
        setBulkDeleteRows(rows);
      } catch (err) {
        console.error(err);
        message.error("Failed to parse file");
      }
    };
    reader.readAsArrayBuffer(file);
    return false; // prevent upload
  };

  const handleBulkDelete = async () => {
    if (!bulkDeleteRows.length) {
      message.warning("Please upload a file with data first");
      return;
    }
    setBulkSubmitting(true);
    try {
      const skus = bulkDeleteRows.map(row => row.sku);
      const result = await bulkDeleteAlertWarnings({ skus, counterCode: "ECOMMRI" });
      message.success(result.data.message);
      setBulkDeleteRows([]);
      setIsBulkDeleteModalOpen(false);
      callReFetch();
    } catch (error) {
      message.error("Failed to delete items");
    } finally {
      setBulkSubmitting(false);
    }
  };

  const handleOpenBulkDelete = () => {
    setBulkDeleteRows([]);
    setIsBulkDeleteModalOpen(true);
  };

  const {
    fetchedData: { data },
    callReFetch,
    loading,
  } = useFetch(`${FETCH_ALL_ALERTWARNING_API}`, state);

  const {
    fetchedData: { data: counterData },
    callReFetchCounter,
    loadingCounter,
  } = useFetch(`${FETCH_ALL_COUNTER_API}`, state);

  const handleSearch = (value) => {
    dispatch({ type: "searchKeyword", payload: value });
    callReFetch();
  };

  return (
    <div
      style={{
        width: "100vw",
        padding: "20px",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <div
        style={{
          paddingBottom: "20px",
          display: "flex",
          justifyContent: "space-between",
          width: "50vw",
          maxWidth: "100vw",
        }}
      >
        <Button type="primary" onClick={handleOpenModal}>
          Create New Alert Warning
        </Button>
        <Button onClick={handleOpenBulk}>Bulk Upload</Button>
        <Button 
          danger 
          onClick={handleOpenBulkDelete}
        >
          Bulk Delete
        </Button>
        <Search
          placeholder="Search..."
          allowClear
          onSearch={handleSearch}
          style={{ width: "300px" }}
        />
      </div>

      <AlertWarningTable
        history={history}
        loading={loading}
        data={data}
        counterData={counterData}
        callReFetch={callReFetch}
        dispatch={dispatch}
        handleDelete={handleDelete}
      />

      <Modal
        title="Create Alert Warning"
        open={isModalVisible}
        footer={null}
        onCancel={handleCloseModal}
      >
        <AlertWarningCreationForm
          history={history}
          userData={userData}
          counterData={counterData}
          callReFetch={callReFetch}
          onClose={handleCloseModal}
        />
      </Modal>

      <Modal
        title="Bulk Upload Alert Warnings"
        open={isBulkModalOpen}
        onCancel={() => setIsBulkModalOpen(false)}
        onOk={handleBulkSubmit}
        okButtonProps={{ loading: bulkSubmitting }}
        width={700}
      >
        <p>Upload an Excel/CSV file with columns: sku, threshold.</p>
        <Upload beforeUpload={parseFile} maxCount={1} accept=".xlsx,.xls,.csv">
          <Button icon={<UploadOutlined />}>Select File</Button>
        </Upload>
        <div style={{ marginTop: 16 }}>
          <Table
            size="small"
            dataSource={bulkRows.map((r, idx) => ({ ...r, key: idx }))}
            columns={[
              { title: "SKU", dataIndex: "sku" },
              { title: "Threshold", dataIndex: "threshold" },
            ]}
            pagination={{ pageSize: 10 }}
          />
        </div>
      </Modal>

      <Modal
        title="Delete Alert Warning"
        open={isModalOpen}
        onOk={handleOk}
        onCancel={() => setIsModalOpen(false)}
      >
        <p>Are you sure you want to delete this alert warning?</p>
      </Modal>

      <Modal
        title="Bulk Delete Alert Warnings"
        open={isBulkDeleteModalOpen}
        onOk={handleBulkDelete}
        onCancel={() => setIsBulkDeleteModalOpen(false)}
        okButtonProps={{ loading: bulkSubmitting, danger: true }}
        width={700}
      >
        <p>Upload an Excel/CSV file with column: sku to delete alert warnings.</p>
        <Upload beforeUpload={parseDeleteFile} maxCount={1} accept=".xlsx,.xls,.csv">
          <Button icon={<UploadOutlined />}>Select File</Button>
        </Upload>
        <div style={{ marginTop: 16 }}>
          <Table
            size="small"
            dataSource={bulkDeleteRows.map((r, idx) => ({ ...r, key: idx }))}
            columns={[
              { title: "SKU", dataIndex: "sku" },
            ]}
            pagination={{ pageSize: 10 }}
          />
        </div>
        {bulkDeleteRows.length > 0 && (
          <p style={{ color: 'red', fontWeight: 'bold', marginTop: 16 }}>
            Are you sure you want to delete {bulkDeleteRows.length} alert warning(s)? This action cannot be undone.
          </p>
        )}
      </Modal>
    </div>
  );
};

AlertWarningPage.propTypes = {
  history: PropTypes.object,
  userData: PropTypes.object,
};

export default AlertWarningPage;
